import { useId } from 'react';

import { Field } from '../../components/ui/Field';
import { Select } from '../../components/ui/form';
import { KEY_OPTIONS, MAX_BPM, MIN_BPM } from './keys';

/**
 * Tempo and key, as one row, in both forms that ask for them.
 *
 * ## Filled in, and said so
 *
 * Every upload is analysed for tempo and key once the transcode has finished,
 * and a field the artist left blank is filled from the measurement when it
 * lands (`AudioAnalysis::adopt`). So the editor usually opens with numbers
 * already in it. What it owes the artist is to say where they came from: the
 * "heard" row under the fields shows the measurement, lit when the field
 * still matches it, so a number the platform guessed and a number the artist
 * confirmed do not look the same. A field the artist typed before the answer
 * arrived is never overwritten; the row then shows the disagreement.
 *
 * ## The octave is offered, not decided
 *
 * A 70 BPM trap beat comes back as 140 and the audio does not say which. The
 * analyser gives one number and a confidence; when the confidence is below
 * "solid" the half and the double are offered beside it, because those are the
 * only two ways it is ever wrong by a lot. The artist wrote the track.
 *
 * ## Both optional, and said so
 *
 * Plenty of music has no meaningful BPM and plenty of artists have no idea what
 * key theirs is in. A required field here would produce a column full of
 * confident nonsense, which is worse for the mixing this exists to enable than
 * a column full of nulls.
 *
 * One component rather than two copies because the bounds and the wording have
 * to match between uploading and editing - the way a pair like this goes wrong
 * is one side being changed and the other not.
 */
/** What the analyser heard. Null on a track that was never analysed. */
export interface Analysis {
  bpm: number | null;
  key: string | null;
  /** essentia's rhythm confidence, 0 to 5.32: under 1.5 unreliable, over 3.5 solid. */
  bpm_confidence: number | null;
  /** 0 to 1: under 0.5 unknown, 0.7 and up confident. */
  key_strength: number | null;
  engine: string | null;
}

/**
 * essentia's rhythm confidence, above which one number is enough. Below it
 * the half and the double are offered too. There is no floor beneath which a
 * measurement is withheld: the field is already filled with it (see
 * `AudioAnalysis::adopt`), so the honest thing is to show it and say so.
 */
const BPM_SOLID = 3.5;

export function TempoAndKey({
  bpm,
  keySignature,
  analysis = null,
  disabled = false,
  onBpm,
  onKey,
}: {
  /** Held as a string so the field can be empty, which is a real answer. */
  bpm: string;
  keySignature: string;
  analysis?: Analysis | null;
  disabled?: boolean;
  onBpm: (value: string) => void;
  onKey: (value: string) => void;
}) {
  const keyId = useId();

  /*
   * What to offer. One number when solid; the number with its half and
   * double when not, kept inside the bounds the field enforces.
   */
  const heardBpm = analysis?.bpm ?? null;
  const bpmUnsure = heardBpm !== null && (analysis?.bpm_confidence ?? 0) < BPM_SOLID;

  const candidates =
    heardBpm === null
      ? []
      : [heardBpm, ...(bpmUnsure ? [Math.round(heardBpm / 2), heardBpm * 2] : [])].filter(
          (n, i, all) => n >= MIN_BPM && n <= MAX_BPM && all.indexOf(n) === i,
        );

  const heardKey = analysis?.key ?? null;

  // Both fields still say what the analyser did: the one case worth a
  // sentence, because the artist may not know these were not theirs.
  const asHeard =
    (heardBpm === null || String(heardBpm) === bpm.trim()) &&
    (heardKey === null || heardKey === keySignature) &&
    (heardBpm !== null || heardKey !== null);

  const typed = bpm.trim();
  const number = Number(typed);

  /*
   * Checked here as well as on the server.
   *
   * Not because the server's check can be skipped - it cannot - but because a
   * 422 arriving after the upload button is a poor way to learn you typed the
   * year instead of the tempo.
   */
  const outOfRange =
    typed !== '' && (!Number.isFinite(number) || number < MIN_BPM || number > MAX_BPM);

  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <Field
        label="BPM"
        name="bpm"
        type="number"
        inputMode="numeric"
        min={MIN_BPM}
        max={MAX_BPM}
        step={1}
        value={bpm}
        disabled={disabled}
        data-track-bpm
        placeholder="Optional"
        error={outOfRange ? `Between ${MIN_BPM} and ${MAX_BPM}.` : undefined}
        onChange={(event) => onBpm(event.target.value)}
      />

      <div className="flex flex-col gap-1.5">
        <label htmlFor={keyId} className="text-sm font-medium text-soft">
          Key
        </label>
        <Select
          id={keyId}
          value={keySignature}
          disabled={disabled}
          data-track-key
          placeholder="Optional"
          options={KEY_OPTIONS}
          onChange={onKey}
        />
        {heardKey !== null && (
          <button
            type="button"
            disabled={disabled}
            data-heard-key={heardKey}
            onClick={() => onKey(heardKey)}
            className={`self-start rounded-[var(--radius-pill)] border px-2.5 py-1 font-mono text-[11px] transition-colors ${
              heardKey === keySignature
                ? 'border-accent bg-accent/10 text-accent'
                : 'border-line text-soft hover:border-line-strong hover:text-ink'
            }`}
          >
            heard {keyLabel(heardKey)}
          </button>
        )}
      </div>

      {candidates.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 sm:col-span-2" data-heard-bpm>
          <span className="font-mono text-[11px] text-faint">{bpmUnsure ? 'heard one of' : 'heard'}</span>
          {candidates.map((candidate) => (
            <button
              key={candidate}
              type="button"
              disabled={disabled}
              onClick={() => onBpm(String(candidate))}
              className={`rounded-[var(--radius-pill)] border px-2.5 py-1 font-mono text-[11px] transition-colors ${
                String(candidate) === bpm.trim()
                  ? 'border-accent bg-accent/10 text-accent'
                  : 'border-line text-soft hover:border-line-strong hover:text-ink'
              }`}
            >
              {candidate}
            </button>
          ))}
          {bpmUnsure && (
            /* The honest reason there is a choice. A track that repeats every
               beat also repeats every two, and the audio does not say which. */
            <span className="font-mono text-[11px] text-faint">· it may be the half or the double</span>
          )}
        </div>
      )}

      {asHeard && (
        <p className="-mt-3 font-mono text-[11px] text-faint sm:col-span-2" data-as-heard>
          Filled in from the audio. Change it if it is wrong.
        </p>
      )}
    </div>
  );
}

/** `Dm` as "D minor", for a suggestion somebody reads rather than stores. */
function keyLabel(key: string): string {
  return KEY_OPTIONS.find((option) => option.value === key)?.label ?? key;
}

/**
 * The field's text, as the API wants it.
 *
 * Empty means "no answer", which is null rather than zero - a track nobody has
 * given a tempo for is not a track at 0 BPM, and the difference matters to
 * anything that later tries to mix out of it.
 */
export function bpmForRequest(bpm: string): number | null {
  const typed = bpm.trim();

  return typed === '' ? null : Number(typed);
}
